/**
 * NotificationsPage — /notifications
 * Full-page view of the DCC-03 notification feed.
 *
 * Reuses the same notificationsApi used by the NotificationBell in Layout.jsx.
 * Supports: All / Unread filter, mark-read, mark-all-read, dismiss, pagination.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, BellRing, Check, CheckCheck, X, RefreshCw,
  ExternalLink, Inbox,
} from "lucide-react";
import { notificationsApi } from "@/services/api";
import { toast } from "sonner";

const NOTIF_TYPE_META = {
  DOC_SUBMITTED_FOR_REVIEW: { label: "Document submitted",      color: "#2563EB" },
  DOC_APPROVED:             { label: "Document approved",       color: "#16A34A" },
  DOC_REJECTED:             { label: "Document rejected",       color: "#DC2626" },
  COMM_FAILED:              { label: "Message delivery failed", color: "#D97706" },
  THREAD_MENTION:           { label: "Mentioned in thread",     color: "#7C3AED" },
  EVENT_APPROVAL_REQUESTED: { label: "Approval required",       color: "#EA580C" },
  APPROVAL_CHANGES_REQUIRED: { label: "Changes requested",      color: "#D97706" },
  APPROVAL_WITHDRAWN:        { label: "Approval withdrawn",      color: "#78716C" },
  EVENT_CONFIRMED:          { label: "Event confirmed",         color: "#059669" },
  CLOSEOUT_EXCEPTION:       { label: "Closeout exception",      color: "#B45309" },
  // WVB-03 Budget types
  BUDGET_APPROVAL_REQUESTED: { label: "Budget approval requested", color: "#EA580C" },
  BUDGET_APPROVED:           { label: "Budget approved",           color: "#16A34A" },
  BUDGET_REJECTED:           { label: "Budget rejected",           color: "#DC2626" },
  BUDGET_CHANGES_REQUIRED:   { label: "Budget changes required",   color: "#D97706" },
  // WVB-04 Financial types
  BUDGET_EXCEEDS_CONTRACT:   { label: "Budget exceeds contract",   color: "#DC2626" },
  MARGIN_CRITICAL:           { label: "Margin critical",           color: "#DC2626" },
  COST_BUDGET:               { label: "Cost budget",               color: "#7C3AED" },
  APPROVAL_REQUEST:          { label: "Approval request",          color: "#EA580C" },
};

const PAGE_SIZE = 30;

// WVB-04: Filter tabs include dedicated BUDGET financial filter
const FILTER_TABS = [
  { key: "all",    label: "All" },
  { key: "unread", label: "Unread" },
  { key: "budget", label: "Budget & Finance" },
];

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [filter,         setFilter]         = useState("all");
  const [notifications,  setNotifications]  = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [loadingMore,    setLoadingMore]    = useState(false);
  const [unreadCount,    setUnreadCount]    = useState(0);
  const [hasMore,        setHasMore]        = useState(false);
  const [skip,           setSkip]           = useState(0);
  const loadedOnce                          = useRef(false);

  const fetchNotifs = useCallback(async (resetSkip = true) => {
    const currentSkip = resetSkip ? 0 : skip;
    if (resetSkip) setLoading(true);
    else setLoadingMore(true);

    try {
      const params = { limit: PAGE_SIZE, skip: currentSkip };
      if (filter === "unread") params.status = "UNREAD";
      const res = await notificationsApi.list(params);
      const items = res.data || [];

      if (resetSkip) {
        setNotifications(items);
        setSkip(items.length);
      } else {
        setNotifications(prev => [...prev, ...items]);
        setSkip(currentSkip + items.length);
      }
      setHasMore(items.length === PAGE_SIZE);

      // Also refresh unread count
      const countRes = await notificationsApi.getUnreadCount();
      setUnreadCount(countRes.data?.count ?? 0);
    } catch {
      toast.error("Could not load notifications");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    fetchNotifs(true);
    loadedOnce.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleMarkRead = async (nid) => {
    try {
      await notificationsApi.markRead(nid);
      setNotifications(prev =>
        prev.map(n => n.id === nid ? { ...n, status: "READ" } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      toast.error("Could not mark as read");
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, status: "READ" })));
      setUnreadCount(0);
      toast.success("All notifications marked as read");
    } catch {
      toast.error("Could not mark all as read");
    }
  };

  const handleDismiss = async (nid) => {
    const removed = notifications.find(n => n.id === nid);
    try {
      await notificationsApi.dismiss(nid);
      setNotifications(prev => prev.filter(n => n.id !== nid));
      if (removed?.status === "UNREAD") setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      toast.error("Could not dismiss notification");
    }
  };

  const handleNotifClick = (n) => {
    if (n.status === "UNREAD") handleMarkRead(n.id);
    if (n.deep_link) navigate(n.deep_link);
  };

  const BUDGET_TYPES = new Set([
    "BUDGET_APPROVAL_REQUESTED","BUDGET_APPROVED","BUDGET_REJECTED",
    "BUDGET_CHANGES_REQUIRED","BUDGET_EXCEEDS_CONTRACT","MARGIN_CRITICAL",
    "COST_BUDGET","APPROVAL_REQUEST",
  ]);
  const unreadItems  = notifications.filter(n => n.status === "UNREAD");
  const budgetItems  = notifications.filter(n => BUDGET_TYPES.has(n.notification_type));
  const displayItems = filter === "unread" ? unreadItems
                     : filter === "budget" ? budgetItems
                     : notifications;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-5">

      {/* Page header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {unreadCount > 0
            ? <BellRing className="w-5 h-5" style={{ color: "var(--brand-gold, #B8860B)" }} />
            : <Bell     className="w-5 h-5" style={{ color: "#78716C" }} />
          }
          <div>
            <h1
              className="text-lg font-semibold"
              style={{ color: "#1C1917", fontFamily: "var(--font-display, 'Cormorant Garamond', serif)" }}
            >
              Notifications
            </h1>
            {unreadCount > 0 && (
              <p className="text-xs" style={{ color: "#78716C" }}>
                {unreadCount} unread
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            data-testid="notif-page-refresh-btn"
            onClick={() => fetchNotifs(true)}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors"
            style={{ color: "#57534E", border: "1px solid #E7E0D8", background: "#FFFFFF" }}
            onMouseEnter={e => { e.currentTarget.style.background = "#F0EAE0"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#FFFFFF"; }}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          {unreadCount > 0 && (
            <button
              data-testid="notif-page-mark-all-read-btn"
              onClick={handleMarkAllRead}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
              style={{ color: "#2563EB", border: "1px solid #BFDBFE", background: "#EFF6FF" }}
              onMouseEnter={e => { e.currentTarget.style.background = "#DBEAFE"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#EFF6FF"; }}
            >
              <CheckCheck className="w-3.5 h-3.5" />
              Mark all read
            </button>
          )}
        </div>
      </div>

      {/* Filter tabs */}
      <div
        className="flex gap-1 p-1 rounded-xl"
        style={{ background: "#F0EAE0", width: "fit-content" }}
      >
        {FILTER_TABS.map(tab => (
          <button
            key={tab.key}
            data-testid={`notif-filter-${tab.key}`}
            onClick={() => setFilter(tab.key)}
            className="px-4 py-1.5 rounded-lg text-sm font-medium transition-all"
            style={filter === tab.key
              ? { background: "#FFFFFF", color: "#1C1917", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }
              : { color: "#78716C", background: "transparent" }
            }
          >
            {tab.label}
            {tab.key === "unread" && unreadCount > 0 && (
              <span
                className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold"
                style={{ background: "#DC2626", color: "#FFFFFF" }}
              >
                {unreadCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{ border: "1px solid #E7E0D8", background: "#FFFFFF" }}
      >
        {/* Loading skeleton */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="w-5 h-5 animate-spin" style={{ color: "#D6CEBF" }} />
          </div>
        )}

        {/* Empty state */}
        {!loading && displayItems.length === 0 && (
          <div
            data-testid="notif-page-empty-state"
            className="flex flex-col items-center justify-center py-16 gap-3"
          >
            <Inbox className="w-10 h-10" style={{ color: "#D6CEBF" }} />
            <p className="text-sm font-medium" style={{ color: "#78716C" }}>
              {filter === "unread" ? "No unread notifications" : "No notifications yet"}
            </p>
            <p className="text-xs text-center max-w-xs" style={{ color: "#A8A29E" }}>
              {filter === "unread"
                ? "You're all caught up."
                : "Approval requests, document updates and event alerts will appear here."}
            </p>
          </div>
        )}

        {/* Notification list */}
        {!loading && displayItems.length > 0 && (
          <div>
            {displayItems.map((n, idx) => {
              const meta  = NOTIF_TYPE_META[n.notification_type] || { label: n.notification_type, color: "#57534E" };
              const isNew = n.status === "UNREAD";
              return (
                <div
                  key={n.id}
                  data-testid={`notif-page-item-${n.id}`}
                  className="group flex items-start gap-4 px-5 py-4 cursor-pointer transition-colors"
                  style={{
                    background: isNew ? "#FAFAF9" : "#FFFFFF",
                    borderBottom: idx < displayItems.length - 1 ? "1px solid #F5F0EB" : "none",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#F5F0EB"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = isNew ? "#FAFAF9" : "#FFFFFF"; }}
                  onClick={() => handleNotifClick(n)}
                >
                  {/* Status indicator */}
                  <div className="flex-shrink-0 mt-1.5">
                    <div
                      className="w-2.5 h-2.5 rounded-full ring-2 ring-white"
                      style={{ background: isNew ? meta.color : "#E7E0D8" }}
                    />
                  </div>

                  {/* Body */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <p
                        className="text-sm leading-snug"
                        style={{ color: "#1C1917", fontWeight: isNew ? 600 : 400 }}
                      >
                        {n.title}
                      </p>
                      <div className="flex-shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {isNew && (
                          <button
                            data-testid={`notif-page-read-${n.id}`}
                            onClick={(e) => { e.stopPropagation(); handleMarkRead(n.id); }}
                            className="p-1 rounded transition-colors"
                            style={{ color: "#2563EB" }}
                            title="Mark as read"
                            onMouseEnter={e => { e.currentTarget.style.background = "#EFF6FF"; }}
                            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          data-testid={`notif-page-dismiss-${n.id}`}
                          onClick={(e) => { e.stopPropagation(); handleDismiss(n.id); }}
                          className="p-1 rounded transition-colors"
                          style={{ color: "#78716C" }}
                          title="Dismiss"
                          onMouseEnter={e => { e.currentTarget.style.background = "#F0EAE0"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {n.description && (
                      <p className="text-sm mt-0.5 leading-relaxed" style={{ color: "#78716C" }}>
                        {n.description}
                      </p>
                    )}

                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span
                        className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                        style={{ background: `${meta.color}18`, color: meta.color }}
                      >
                        {meta.label}
                      </span>
                      <span className="text-[10px]" style={{ color: "#A8A29E" }}>
                        {new Date(n.created_at).toLocaleDateString(undefined, {
                          month: "short", day: "numeric",
                          hour: "2-digit", minute: "2-digit",
                        })}
                      </span>
                      {n.deep_link && (
                        <span
                          className="flex items-center gap-0.5 text-[10px] font-medium"
                          style={{ color: "#2563EB" }}
                        >
                          <ExternalLink className="w-2.5 h-2.5" />
                          View
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Load more */}
            {hasMore && (
              <div
                className="flex items-center justify-center py-4"
                style={{ borderTop: "1px solid #F5F0EB" }}
              >
                <button
                  data-testid="notif-page-load-more-btn"
                  onClick={() => fetchNotifs(false)}
                  disabled={loadingMore}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  style={{ color: "#2563EB", border: "1px solid #BFDBFE", background: "#EFF6FF" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#DBEAFE"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#EFF6FF"; }}
                >
                  {loadingMore
                    ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Loading...</>
                    : "Load more"
                  }
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <p
        className="text-xs text-center"
        style={{ color: "#A8A29E" }}
        data-testid="notif-page-footer-note"
      >
        Notifications are retained for 30 days.
      </p>
    </div>
  );
}
